import React, { useState, useEffect, useRef } from 'react';
import './CustomDropdown.scss';

interface Option {
  label: string;
  value: string;
}

interface CustomDropdownProps {
  options: Option[];
  selectedValue: string;
  onChange: (value: string) => void;
  allowMultiple?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ options, selectedValue, onChange, allowMultiple = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(allowMultiple ? [] : [selectedValue]);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (value: string) => {
    if (allowMultiple) {
      setSelectedOptions((prev) => {
        const newSelection = prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value];
        return newSelection;
      });
    } else {
      setSelectedOptions([value]);
      setIsOpen(false);
    }
    onChange(value);
  };

  useEffect(() => {
    if (!allowMultiple) {
      setSelectedOptions([selectedValue]);
    }
  }, [selectedValue, allowMultiple]);

  const placeholder = 'everything';

  return (
    <div className="custom-dropdown" ref={dropdownRef}>
      <div className="custom-dropdown-selected" onClick={() => setIsOpen(!isOpen)}>
        {selectedOptions.length > 0 ? options.find(option => option.value === selectedOptions[0])?.label : placeholder}
      </div>
      {isOpen && (
        <div className="custom-dropdown-options">
          {options.map((option) => (
            <div
              key={option.value}
              className={`custom-dropdown-option ${selectedOptions.includes(option.value) ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;